import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Building2,
  Phone,
  Mail,
  Globe,
  MapPin,
  Edit,
  Trash2,
  Plus,
  User,
  Star,
  ArrowLeft,
  MoreVertical,
  RefreshCw,
  Send,
  ExternalLink,
  CheckCircle,
  Clock,
  XCircle,
  Calendar,
  Camera,
  Image,
  CalendarClock,
  Briefcase,
  Search,
  Filter,
  ArrowUpDown,
  Archive,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useCompany, useDeleteCompany, useContacts, useDeleteContact, useSendPartnerInvite } from '@/hooks/useCompanies';
import { useAuth } from '@/contexts/AuthContext';
import { usePageTour, WelcomeModal } from '@/walkthrough';
import { companyDetailPageTour } from '@/walkthrough/tourSteps';
import { canEditCompany, updateCompany as updateCompanyService } from '@/services/companies';
import { ContactFormModal } from '@/components/ContactFormModal';
import { ProjectDetailModal } from '@/components/crm/ProjectDetailModal';
import { MarkCommissionPaidModal } from '@/components/crm/MarkCommissionPaidModal';
import { createOutboundReferral, listOutboundReferrals } from '@/services/outboundReferrals';
import { getProjectsByReferralSource, formatCurrency } from '@/services/projects';
import {
  createVisit,
  getCompanyVisits,
  uploadVisitPhoto,
  calculateVisitStatus,
  getVisitStatusColor,
  formatDaysSinceVisit,
} from '@/services/visits';
import type { Contact, OutboundReferral, Business, OutboundReferralStatus, CompanyVisit, VisitStatus, Project } from '@/types';

export function CompanyDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isOwnerOrAdmin } = useAuth();
  const { showWelcomeModal, startTour, skipTour, globalOptOut } = usePageTour(companyDetailPageTour);

  const { data: company, isLoading: companyLoading } = useCompany(id);
  const { data: contacts, isLoading: contactsLoading } = useContacts(id);
  const deleteCompanyMutation = useDeleteCompany();
  const deleteContactMutation = useDeleteContact(id!);
  const sendInviteMutation = useSendPartnerInvite();

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);

  // Outbound referrals state
  const [showSendReferralModal, setShowSendReferralModal] = useState(false);
  const [outboundReferrals, setOutboundReferrals] = useState<OutboundReferral[]>([]);
  const [loadingReferrals, setLoadingReferrals] = useState(false);

  // Project detail modal
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  // Projects from this partner (referral source)
  const [referredProjects, setReferredProjects] = useState<Project[]>([]);
  const [loadingProjects, setLoadingProjects] = useState(false);

  // Visit tracking state
  const [visits, setVisits] = useState<CompanyVisit[]>([]);
  const [loadingVisits, setLoadingVisits] = useState(false);
  const [showLogVisitModal, setShowLogVisitModal] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState<string | null>(null);

  // Fetch outbound referrals for this company
  useEffect(() => {
    async function fetchReferrals() {
      if (!id) return;
      setLoadingReferrals(true);
      try {
        const result = await listOutboundReferrals(id);
        if (result.success && result.referrals) {
          setOutboundReferrals(result.referrals);
        }
      } catch (error) {
        console.error('Failed to fetch outbound referrals:', error);
      } finally {
        setLoadingReferrals(false);
      }
    }
    fetchReferrals();
  }, [id]);

  // Fetch projects referred by this company
  useEffect(() => {
    async function fetchProjects() {
      if (!id) return;
      setLoadingProjects(true);
      try {
        const projects = await getProjectsByReferralSource(id);
        setReferredProjects(projects);
      } catch (error) {
        console.error('Failed to fetch referred projects:', error);
      } finally {
        setLoadingProjects(false);
      }
    }
    fetchProjects();
  }, [id]);

  // Fetch visit history for this company
  const fetchVisits = async () => {
    if (!id) return;
    setLoadingVisits(true);
    try {
      const result = await getCompanyVisits(id);
      if (result.success && result.visits) {
        setVisits(result.visits);
      }
    } catch (error) {
      console.error('Failed to fetch visits:', error);
    } finally {
      setLoadingVisits(false);
    }
  };

  useEffect(() => {
    // Skip fetching visits if visit tracking is disabled for this company
    if (company?.skipVisitTracking) return;
    fetchVisits();
  }, [id, company?.skipVisitTracking]);

  const canEdit = company && user ? canEditCompany(company, user.id, isOwnerOrAdmin) : false;

  const handleDeleteCompany = async () => {
    if (!id) return;

    try {
      await deleteCompanyMutation.mutateAsync(id);
      navigate('/companies');
    } catch (error) {
      // Error handled by mutation
    }
  };

  const handleDeleteContact = async (contactId: string) => {
    if (!confirm('Are you sure you want to delete this contact?')) return;

    try {
      await deleteContactMutation.mutateAsync(contactId);
    } catch (error) {
      // Error handled by mutation
    }
  };

  if (companyLoading) {
    return (
      <div className="card">
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="animate-spin text-blue-600" size={32} />
        </div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="card">
        <div className="text-center py-8">
          <Building2 size={48} className="mx-auto mb-4 text-gray-300" />
          <p className="text-gray-600">Company not found</p>
          <Link to="/companies" className="btn btn-primary mt-4">
            Back to Companies
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Back button */}
      <Link
        to="/companies"
        className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900"
      >
        <ArrowLeft size={20} />
        Back to Companies
      </Link>

      {/* Company header */}
      <div data-tour="company-header" className="card">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex items-start gap-4">
            <div className="w-16 h-16 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
              <Building2 className="text-blue-600" size={32} />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold text-gray-900">{company.name}</h1>
                {company.type && <span className="badge badge-blue">{company.type}</span>}
              </div>
              {company.notes && (
                <p className="mt-2 text-gray-600">{company.notes}</p>
              )}
            </div>
          </div>

          <div className="flex gap-2">
            {/* Send Invite button - shown if company has email */}
            {company.email && (
              <button
                onClick={() => setShowInviteModal(true)}
                className="btn btn-primary"
              >
                <Send size={18} />
                Send Invite
              </button>
            )}
            {canEdit && (
              <>
                <button
                  onClick={async () => {
                    try {
                      await updateCompanyService(id!, { isInactive: !company.isInactive } as any);
                      toast.success(company.isInactive ? 'Company marked as active' : 'Company marked as inactive');
                      window.location.reload();
                    } catch (err) {
                      toast.error('Failed to update status');
                    }
                  }}
                  className="btn btn-secondary"
                >
                  <Archive size={18} />
                  {company.isInactive ? 'Make Active' : 'Make Inactive'}
                </button>
                <Link to={`/companies/${id}/edit`} className="btn btn-secondary">
                  <Edit size={18} />
                  Edit
                </Link>
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="btn btn-danger"
                >
                  <Trash2 size={18} />
                  Delete
                </button>
              </>
            )}
          </div>
        </div>

        {/* Contact info grid */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {company.phone && (
            <a
              href={`tel:${company.phone}`}
              className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100"
            >
              <Phone className="text-gray-500" size={20} />
              <span className="text-gray-900">{company.phone}</span>
            </a>
          )}
          {company.email && (
            <a
              href={`mailto:${company.email}`}
              className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100"
            >
              <Mail className="text-gray-500" size={20} />
              <span className="text-gray-900">{company.email}</span>
            </a>
          )}
          {company.website && (
            <a
              href={company.website.startsWith('http') ? company.website : `https://${company.website}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100"
            >
              <Globe className="text-gray-500" size={20} />
              <span className="text-gray-900">{company.website}</span>
            </a>
          )}
          {(company.address || company.city || company.state || company.zip) && (
            <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
              <MapPin className="text-gray-500 flex-shrink-0" size={20} />
              <div className="text-gray-900">
                {company.address && <div>{company.address}</div>}
                <div>
                  {[company.city, company.state].filter(Boolean).join(', ')}
                  {company.zip && ` ${company.zip}`}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Contacts section */}
      <div data-tour="contacts-section" className="card">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Contacts</h2>
          <button
            onClick={() => {
              setEditingContact(null);
              setContactModalOpen(true);
            }}
            className="btn btn-primary"
          >
            <Plus size={18} />
            Add Contact
          </button>
        </div>

        {contactsLoading ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="animate-spin text-blue-600" size={24} />
          </div>
        ) : contacts && contacts.length > 0 ? (
          <div className="divide-y">
            {contacts.map((contact) => (
              <div
                key={contact.id}
                className="py-4 flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                    <User className="text-gray-500" size={20} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900">
                        {contact.firstName} {contact.lastName}
                      </span>
                      {contact.isPrimary && (
                        <Star className="text-yellow-500 fill-yellow-500" size={16} />
                      )}
                    </div>
                    {contact.title && (
                      <p className="text-sm text-gray-600">{contact.title}</p>
                    )}
                    <div className="flex gap-4 mt-1 text-sm text-gray-500">
                      {contact.email && (
                        <a href={`mailto:${contact.email}`} className="hover:text-blue-600">
                          {contact.email}
                        </a>
                      )}
                      {contact.phone && (
                        <a href={`tel:${contact.phone}`} className="hover:text-blue-600">
                          {contact.phone}
                        </a>
                      )}
                    </div>
                    {/* Contact-level overrides */}
                    <div className="flex gap-2 mt-1 flex-wrap">
                      {contact.salesRepName && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                          Rep: {contact.salesRepName}
                        </span>
                      )}
                      {contact.commissionType && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">
                          {contact.commissionType === 'percentage'
                            ? `${((contact.commissionValue || 0) * 100).toFixed(1)}%`
                            : `$${(contact.commissionValue || 0).toLocaleString()}`}
                          {contact.commissionCap ? ` (cap $${contact.commissionCap.toLocaleString()})` : ''}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setEditingContact(contact);
                      setContactModalOpen(true);
                    }}
                    className="p-2 text-gray-500 hover:text-blue-600 hover:bg-gray-100 rounded"
                  >
                    <Edit size={18} />
                  </button>
                  <button
                    onClick={() => handleDeleteContact(contact.id)}
                    className="p-2 text-gray-500 hover:text-red-600 hover:bg-gray-100 rounded"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <User size={40} className="mx-auto mb-2 opacity-50" />
            <p>No contacts added yet</p>
          </div>
        )}
      </div>

      {/* Company Notes section - always visible */}
      <CompanyNotesSection companyId={id!} initialNotes={company.notes || ''} canEdit={canEdit} />

      {/* Visit Tracking section - hidden for internet/non-physical companies */}
      {!company.skipVisitTracking && (
        <div data-tour="visit-tracking" className="card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Visit History</h2>
              <p className="text-sm text-gray-500">Track your visits to this partner</p>
            </div>
            <button
              onClick={() => setShowLogVisitModal(true)}
              className="btn btn-primary"
            >
              <Calendar size={18} />
              Log Visit
            </button>
          </div>

          {/* Visit Status Indicator */}
          {company && (
            <VisitStatusIndicator
              lastVisitDate={company.lastVisitDate}
              nextFollowUpDate={company.nextFollowUpDate}
            />
          )}

          {loadingVisits ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="animate-spin text-blue-600" size={24} />
            </div>
          ) : visits.length > 0 ? (
            <div className="divide-y mt-4">
              {visits.map((visit) => (
                <div key={visit.id} className="py-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <Calendar size={16} className="text-gray-400" />
                        <span className="font-medium text-gray-900">
                          {new Date(visit.visitDate).toLocaleDateString('en-US', {
                            weekday: 'short',
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                          })}
                        </span>
                        <span className="text-sm text-gray-500">by {visit.visitedByName}</span>
                      </div>
                      {visit.notes && (
                        <p className="text-sm text-gray-600 mt-2">{visit.notes}</p>
                      )}
                      {visit.evidencePhotos && visit.evidencePhotos.length > 0 && (
                        <div className="flex gap-2 mt-3">
                          {visit.evidencePhotos.map((photo) => (
                            <button
                              key={photo.id}
                              onClick={() => setSelectedPhoto(photo.downloadURL)}
                              className="relative group cursor-pointer"
                            >
                              <img
                                src={photo.downloadURL}
                                alt={photo.fileName}
                                className="w-16 h-16 object-cover rounded-lg border border-gray-200"
                              />
                              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 rounded-lg flex items-center justify-center transition-opacity">
                                <Image size={20} className="text-white" />
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500 mt-4">
              <Calendar size={40} className="mx-auto mb-2 opacity-50" />
              <p>No visits logged yet</p>
              <button
                onClick={() => setShowLogVisitModal(true)}
                className="btn btn-secondary mt-4"
              >
                <Plus size={18} />
                Log First Visit
              </button>
            </div>
          )}
        </div>
      )}

      {/* Log Visit Modal */}
      {showLogVisitModal && company && (
        <LogVisitModal
          company={company}
          onClose={() => setShowLogVisitModal(false)}
          onSuccess={async () => {
            // Refetch visits and company data to update status
            await fetchVisits();
            window.location.reload(); // Simple way to refresh company data
            setShowLogVisitModal(false);
          }}
        />
      )}

      {/* Photo Modal */}
      {selectedPhoto && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
          onClick={() => setSelectedPhoto(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh] p-4">
            <button
              onClick={() => setSelectedPhoto(null)}
              className="absolute top-2 right-2 w-10 h-10 bg-white rounded-full flex items-center justify-center text-gray-700 hover:bg-gray-100"
            >
              ×
            </button>
            <img
              src={selectedPhoto}
              alt="Visit evidence"
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}

      {/* Projects Section - Two Column Layout */}
      <div data-tour="projects-section">
        <ProjectsSection
          referredProjects={referredProjects}
          outboundReferrals={outboundReferrals}
          loadingProjects={loadingProjects}
          loadingReferrals={loadingReferrals}
          onSendReferral={() => setShowSendReferralModal(true)}
          onSelectProject={(project) => setSelectedProject(project)}
        />
      </div>

      {/* Send Referral Modal */}
      {showSendReferralModal && company && (
        <SendReferralModal
          company={company}
          onClose={() => setShowSendReferralModal(false)}
          onSuccess={(referral) => {
            setOutboundReferrals([referral, ...outboundReferrals]);
            setShowSendReferralModal(false);
          }}
        />
      )}

      {/* Project Detail Modal */}
      {selectedProject && (
        <ProjectDetailModal
          project={selectedProject}
          onClose={() => setSelectedProject(null)}
        />
      )}

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900">Delete Company</h3>
            <p className="mt-2 text-gray-600">
              Are you sure you want to delete <strong>{company.name}</strong>? This action
              cannot be undone and will also delete all associated contacts.
            </p>
            <div className="mt-6 flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteCompany}
                disabled={deleteCompanyMutation.isPending}
                className="btn btn-danger"
              >
                {deleteCompanyMutation.isPending ? (
                  <>
                    <RefreshCw className="animate-spin" size={18} />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 size={18} />
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contact form modal */}
      {contactModalOpen && id && (
        <ContactFormModal
          companyId={id}
          contact={editingContact}
          onClose={() => {
            setContactModalOpen(false);
            setEditingContact(null);
          }}
        />
      )}

      {/* Send Invite modal */}
      {showInviteModal && company && (
        <SendInviteModal
          company={company}
          contacts={contacts || []}
          onClose={() => setShowInviteModal(false)}
          onSend={async (email, name) => {
            await sendInviteMutation.mutateAsync({
              companyId: company.id,
              companyName: company.name,
              contactEmail: email,
              contactName: name,
            });
            setShowInviteModal(false);
          }}
          isSending={sendInviteMutation.isPending}
        />
      )}

      <WelcomeModal
        pageName={companyDetailPageTour.pageName}
        onStartTour={startTour}
        onSkip={skipTour}
        onGlobalOptOut={globalOptOut}
        open={showWelcomeModal}
      />
    </div>
  );
}

// ===========================================
// Projects Section Component (Two Column Layout)
// ===========================================

type ProjectFilter = 'all' | 'active' | 'archived';
type ProjectSort = 'date' | 'status' | 'name';

function isProjectArchived(project: Project): boolean {
  const status = (project.status || '').toLowerCase();
  return status.includes('complete') || status.includes('closed');
}

interface ProjectsSectionProps {
  referredProjects: Project[];
  outboundReferrals: OutboundReferral[];
  loadingProjects: boolean;
  loadingReferrals: boolean;
  onSendReferral: () => void;
  onSelectProject: (project: Project) => void;
}

function ProjectsSection({
  referredProjects,
  outboundReferrals,
  loadingProjects,
  loadingReferrals,
  onSendReferral,
  onSelectProject,
}: ProjectsSectionProps) {
  const [commissionProject, setCommissionProject] = useState<Project | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<ProjectFilter>('all');
  const [sort, setSort] = useState<ProjectSort>('date');

  // Filter and sort projects from partner
  const filteredProjects = useMemo(() => {
    let projects = [...referredProjects];

    // Filter by search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      projects = projects.filter((p) =>
        p.name.toLowerCase().includes(query)
      );
    }

    // Filter by status
    if (filter === 'active') {
      projects = projects.filter((p) => !isProjectArchived(p));
    } else if (filter === 'archived') {
      projects = projects.filter((p) => isProjectArchived(p));
    }

    // Sort
    projects.sort((a, b) => {
      switch (sort) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'status':
          return (a.status || '').localeCompare(b.status || '');
        case 'date':
        default:
          const dateA = a.closeDate ? new Date(a.closeDate).getTime() : 0;
          const dateB = b.closeDate ? new Date(b.closeDate).getTime() : 0;
          return dateB - dateA;
      }
    });

    return projects;
  }, [referredProjects, searchQuery, filter, sort]);

  // Filter and sort outbound referrals
  const filteredReferrals = useMemo(() => {
    let referrals = [...outboundReferrals];

    // Filter by search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      referrals = referrals.filter((r) =>
        r.clientName.toLowerCase().includes(query)
      );
    }

    // Sort
    referrals.sort((a, b) => {
      const dateA = a.sentAt instanceof Date ? a.sentAt.getTime() : new Date((a.sentAt as any).toDate?.() || a.sentAt).getTime();
      const dateB = b.sentAt instanceof Date ? b.sentAt.getTime() : new Date((b.sentAt as any).toDate?.() || b.sentAt).getTime();
      return dateB - dateA;
    });

    return referrals;
  }, [outboundReferrals, searchQuery]);

  const activeCount = referredProjects.filter((p) => !isProjectArchived(p)).length;
  const archivedCount = referredProjects.filter((p) => isProjectArchived(p)).length;

  return (
    <div className="card">
      {/* Header with Search and Filters */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Projects & Referrals</h2>

        {/* Search Bar */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            placeholder="Search projects by name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input pl-10"
          />
        </div>

        {/* Filters and Sort */}
        <div className="flex flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <Filter size={16} className="text-gray-400" />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as ProjectFilter)}
              className="input py-1.5 text-sm"
            >
              <option value="all">All ({referredProjects.length})</option>
              <option value="active">Active ({activeCount})</option>
              <option value="archived">Archived ({archivedCount})</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <ArrowUpDown size={16} className="text-gray-400" />
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value as ProjectSort)}
              className="input py-1.5 text-sm"
            >
              <option value="date">Sort by Date</option>
              <option value="status">Sort by Status</option>
              <option value="name">Sort by Name</option>
            </select>
          </div>

          <button
            onClick={onSendReferral}
            className="btn btn-primary ml-auto"
          >
            <ExternalLink size={18} />
            Send Referral
          </button>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column: Projects From Partner */}
        <div>
          <div className="flex items-center gap-2 mb-3 pb-2 border-b">
            <Briefcase size={18} className="text-blue-600" />
            <h3 className="font-medium text-gray-900">Projects From Partner</h3>
            <span className="text-sm text-gray-500">({filteredProjects.length})</span>
          </div>

          <div className="h-80 overflow-y-auto pr-2">
            {loadingProjects ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="animate-spin text-blue-600" size={24} />
              </div>
            ) : filteredProjects.length > 0 ? (
              <div className="space-y-3">
                {filteredProjects.map((project) => {
                  const archived = isProjectArchived(project);
                  return (
                    <div
                      key={project.id}
                      onClick={() => onSelectProject(project)}
                      className={`p-3 rounded-lg border transition-colors cursor-pointer ${
                        archived
                          ? 'bg-gray-50 border-gray-200 opacity-75'
                          : 'bg-white border-gray-200 hover:border-blue-300'
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <div className="flex-1 min-w-0">
                          {/* Project Name with Portal Link */}
                          <div className="flex items-center gap-2">
                            {project.portalProjectUrl && !archived ? (
                              <a
                                href={project.portalProjectUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-medium text-blue-600 hover:text-blue-800 hover:underline truncate flex items-center gap-1"
                              >
                                {project.name}
                                <ExternalLink size={12} />
                              </a>
                            ) : (
                              <span className={`font-medium truncate ${archived ? 'text-gray-500' : 'text-gray-900'}`}>
                                {project.name}
                              </span>
                            )}
                            {archived && (
                              <Archive size={14} className="text-gray-400 flex-shrink-0" />
                            )}
                          </div>

                          {/* Status and Type */}
                          <div className="flex flex-wrap gap-2 mt-1.5">
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              project.business === 'azdisaster'
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-amber-100 text-amber-700'
                            }`}>
                              {project.business === 'azdisaster' ? 'ACR' : 'Blacksmith'}
                            </span>
                            {project.status && (
                              <span className={`text-xs px-2 py-0.5 rounded inline-flex items-center gap-1 ${
                                archived
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-yellow-100 text-yellow-700'
                              }`}>
                                <span className={`w-1.5 h-1.5 rounded-full ${
                                  archived ? 'bg-green-500' : 'bg-yellow-500'
                                }`} />
                                {project.status}
                              </span>
                            )}
                          </div>

                          {/* Revenue and Type */}
                          {(project.projectType || project.totalRevenue > 0) && (
                            <div className="text-xs text-gray-500 mt-1.5">
                              {project.projectType && <span>{project.projectType}</span>}
                              {project.projectType && project.totalRevenue > 0 && <span> • </span>}
                              {project.totalRevenue > 0 && (
                                <span className="font-medium">{formatCurrency(project.totalRevenue)}</span>
                              )}
                            </div>
                          )}

                          {/* Commission status */}
                          <div className="mt-1.5">
                            {project.partnerCommissionPaid ? (
                              <span className="text-xs text-green-600 font-medium">
                                Paid: {formatCurrency(project.partnerCommissionPaidAmount || project.partnerPaidAmount || 0)}
                              </span>
                            ) : project.partnerExpectedCommission > 0 ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setCommissionProject(project);
                                }}
                                className="text-xs text-amber-600 hover:text-amber-800 underline"
                              >
                                Mark Paid (${project.partnerExpectedCommission?.toLocaleString()})
                              </button>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Briefcase size={32} className="mx-auto mb-2 opacity-50" />
                <p className="text-sm">
                  {searchQuery ? 'No projects match your search' : 'No projects referred yet'}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Right Column: Referrals Sent to Partner */}
        <div data-tour="outbound-referrals">
          <div className="flex items-center gap-2 mb-3 pb-2 border-b">
            <Send size={18} className="text-purple-600" />
            <h3 className="font-medium text-gray-900">Referrals Sent</h3>
            <span className="text-sm text-gray-500">({filteredReferrals.length})</span>
          </div>

          <div className="h-80 overflow-y-auto pr-2">
            {loadingReferrals ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="animate-spin text-blue-600" size={24} />
              </div>
            ) : filteredReferrals.length > 0 ? (
              <div className="space-y-3">
                {filteredReferrals.map((referral) => (
                  <div
                    key={referral.id}
                    className="p-3 rounded-lg border border-gray-200 bg-white hover:border-purple-300 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <span className="font-medium text-gray-900">{referral.clientName}</span>
                      <StatusBadge status={referral.status} />
                    </div>
                    <p className="text-sm text-gray-600 mt-1 line-clamp-2">{referral.description}</p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                      <span className={`px-2 py-0.5 rounded ${
                        referral.business === 'azdisaster'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-amber-100 text-amber-700'
                      }`}>
                        {referral.business === 'azdisaster' ? 'ACR' : 'Blacksmith'}
                      </span>
                      <span>
                        {new Date(referral.sentAt instanceof Date ? referral.sentAt : (referral.sentAt as any).toDate?.() || referral.sentAt).toLocaleDateString()}
                      </span>
                    </div>
                    {referral.clientPhone && (
                      <a href={`tel:${referral.clientPhone}`} className="text-xs text-blue-600 hover:underline mt-2 inline-block">
                        {referral.clientPhone}
                      </a>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Send size={32} className="mx-auto mb-2 opacity-50" />
                <p className="text-sm">
                  {searchQuery ? 'No referrals match your search' : 'No referrals sent yet'}
                </p>
                <button
                  onClick={onSendReferral}
                  className="btn btn-secondary mt-3 text-sm"
                >
                  <Plus size={16} />
                  Send First Referral
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Commission Payment Modal */}
      {commissionProject && (
        <MarkCommissionPaidModal
          project={commissionProject}
          type="partner"
          onClose={() => setCommissionProject(null)}
          onSuccess={() => setCommissionProject(null)}
        />
      )}
    </div>
  );
}

// ===========================================
// Send Invite Modal Component
// ===========================================

interface SendInviteModalProps {
  company: { id: string; name: string; email?: string };
  contacts: Contact[];
  onClose: () => void;
  onSend: (email: string, name: string) => Promise<void>;
  isSending: boolean;
}

function SendInviteModal({ company, contacts, onClose, onSend, isSending }: SendInviteModalProps) {
  const [selectedEmail, setSelectedEmail] = useState(company.email || '');
  const [contactName, setContactName] = useState('');

  // Get primary contact or first contact with email
  const primaryContact = contacts.find((c) => c.isPrimary && c.email) || contacts.find((c) => c.email);

  // Build email options from company email and contacts
  const emailOptions = [
    ...(company.email ? [{ email: company.email, label: `${company.name} (Company)`, name: company.name }] : []),
    ...contacts
      .filter((c) => c.email)
      .map((c) => ({
        email: c.email!,
        label: `${c.firstName} ${c.lastName}${c.isPrimary ? ' (Primary)' : ''}`,
        name: `${c.firstName} ${c.lastName}`,
      })),
  ];

  // Set default selection
  React.useEffect(() => {
    if (primaryContact?.email) {
      setSelectedEmail(primaryContact.email);
      setContactName(`${primaryContact.firstName} ${primaryContact.lastName}`);
    } else if (company.email) {
      setSelectedEmail(company.email);
      setContactName(company.name);
    }
  }, [primaryContact, company]);

  const handleEmailChange = (email: string) => {
    setSelectedEmail(email);
    const option = emailOptions.find((o) => o.email === email);
    if (option) {
      setContactName(option.name);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEmail || !contactName) return;
    await onSend(selectedEmail, contactName);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h3 className="text-lg font-semibold text-gray-900">Send Partner Portal Invite</h3>
        <p className="mt-1 text-sm text-gray-600">
          Send an email invitation to {company.name} with a link to access the partner portal.
        </p>

        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Send To *
            </label>
            {emailOptions.length > 1 ? (
              <select
                value={selectedEmail}
                onChange={(e) => handleEmailChange(e.target.value)}
                className="input"
                required
              >
                {emailOptions.map((opt) => (
                  <option key={opt.email} value={opt.email}>
                    {opt.label} - {opt.email}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="email"
                value={selectedEmail}
                onChange={(e) => setSelectedEmail(e.target.value)}
                className="input"
                required
              />
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Contact Name *
            </label>
            <input
              type="text"
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
              placeholder="Name of the recipient"
              className="input"
              required
            />
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800">
              <strong>Note:</strong> This will send an email with a magic link that allows the partner to view their referred projects and submit notes.
            </p>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary flex-1"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSending || !selectedEmail || !contactName}
              className="btn btn-primary flex-1"
            >
              {isSending ? (
                <>
                  <RefreshCw className="animate-spin" size={18} />
                  Sending...
                </>
              ) : (
                <>
                  <Send size={18} />
                  Send Invite
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ===========================================
// Status Badge Component
// ===========================================

function StatusBadge({ status }: { status: OutboundReferralStatus }) {
  const config: Record<OutboundReferralStatus, { label: string; color: string; icon: React.ReactNode }> = {
    sent: {
      label: 'Sent',
      color: 'bg-gray-100 text-gray-700',
      icon: <Clock size={12} />,
    },
    contacted: {
      label: 'Contacted',
      color: 'bg-blue-100 text-blue-700',
      icon: <Phone size={12} />,
    },
    in_progress: {
      label: 'In Progress',
      color: 'bg-yellow-100 text-yellow-700',
      icon: <Clock size={12} />,
    },
    completed: {
      label: 'Completed',
      color: 'bg-green-100 text-green-700',
      icon: <CheckCircle size={12} />,
    },
    declined: {
      label: 'Declined',
      color: 'bg-red-100 text-red-700',
      icon: <XCircle size={12} />,
    },
  };

  const { label, color, icon } = config[status] || config.sent;

  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded ${color}`}>
      {icon}
      {label}
    </span>
  );
}

// ===========================================
// Send Referral Modal Component
// ===========================================

interface SendReferralModalProps {
  company: { id: string; name: string };
  onClose: () => void;
  onSuccess: (referral: OutboundReferral) => void;
}

// ===========================================
// Visit Status Indicator Component
// ===========================================

interface VisitStatusIndicatorProps {
  lastVisitDate?: Date | string;
  nextFollowUpDate?: Date | string;
}

function VisitStatusIndicator({ lastVisitDate, nextFollowUpDate }: VisitStatusIndicatorProps) {
  const { status, daysSince } = calculateVisitStatus(lastVisitDate || null);
  const colors = getVisitStatusColor(status);

  return (
    <div className={`${colors.bg} rounded-lg p-4`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-3 h-3 rounded-full ${colors.dot}`} />
          <div>
            <p className={`font-medium ${colors.text}`}>
              {status === 'green' && 'Recently Visited'}
              {status === 'yellow' && 'Visit Soon'}
              {status === 'red' && 'Needs Visit'}
            </p>
            <p className="text-sm text-gray-600">
              {formatDaysSinceVisit(daysSince)}
            </p>
          </div>
        </div>
        {nextFollowUpDate && (
          <div className="text-right">
            <p className="text-xs text-gray-500">Next Follow-up</p>
            <p className="text-sm font-medium text-gray-700 flex items-center gap-1">
              <CalendarClock size={14} />
              {new Date(nextFollowUpDate).toLocaleDateString()}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ===========================================
// Log Visit Modal Component
// ===========================================

interface LogVisitModalProps {
  company: { id: string; name: string };
  onClose: () => void;
  onSuccess: () => void | Promise<void>;
}

function LogVisitModal({ company, onClose, onSuccess }: LogVisitModalProps) {
  const [visitDate, setVisitDate] = useState(new Date().toISOString().split('T')[0]);
  const [notes, setNotes] = useState('');
  const [nextFollowUpDate, setNextFollowUpDate] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [photos, setPhotos] = useState<File[]>([]);
  const [photoPreviewUrls, setPhotoPreviewUrls] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    // Limit to 5 photos max
    const newPhotos = [...photos, ...files].slice(0, 5);
    setPhotos(newPhotos);

    // Create preview URLs
    const newPreviewUrls = newPhotos.map((file) => URL.createObjectURL(file));
    // Cleanup old preview URLs
    photoPreviewUrls.forEach((url) => URL.revokeObjectURL(url));
    setPhotoPreviewUrls(newPreviewUrls);
  };

  const removePhoto = (index: number) => {
    const newPhotos = photos.filter((_, i) => i !== index);
    setPhotos(newPhotos);

    URL.revokeObjectURL(photoPreviewUrls[index]);
    const newPreviewUrls = photoPreviewUrls.filter((_, i) => i !== index);
    setPhotoPreviewUrls(newPreviewUrls);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!visitDate) return;

    setIsSaving(true);
    try {
      // Create the visit first
      // Send dates as YYYY-MM-DD to avoid timezone issues
      const result = await createVisit({
        companyId: company.id,
        visitDate: visitDate, // Already in YYYY-MM-DD format from date input
        notes: notes || undefined,
        nextFollowUpDate: nextFollowUpDate || undefined,
      });

      if (!result.success || !result.visitId) {
        toast.error(result.error || 'Failed to log visit');
        return;
      }

      // Upload photos if any
      if (photos.length > 0) {
        let successCount = 0;
        let failCount = 0;

        for (const photo of photos) {
          const photoResult = await uploadVisitPhoto(company.id, result.visitId, photo);
          if (photoResult.success) {
            successCount++;
          } else {
            failCount++;
            console.error('Photo upload failed:', photoResult.error);
          }
        }

        if (failCount > 0) {
          toast.error(`Visit logged but ${failCount} photo(s) failed to upload. Check console for details.`);
        } else {
          toast.success(`Visit logged with ${successCount} photo(s)!`);
        }
      } else {
        toast.success('Visit logged successfully!');
      }

      // Call onSuccess to refetch data
      await onSuccess();
    } catch (error) {
      console.error('Failed to log visit:', error);
      toast.error('Failed to log visit');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold text-gray-900">Log Visit to {company.name}</h3>
        <p className="mt-1 text-sm text-gray-600">
          Record your visit to this referral partner.
        </p>

        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Visit Date *
            </label>
            <input
              type="date"
              value={visitDate}
              onChange={(e) => setVisitDate(e.target.value)}
              className="input"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="What was discussed? What did you bring (gift cards, food, etc.)?"
              rows={3}
              className="input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Schedule Follow-up
            </label>
            <input
              type="date"
              value={nextFollowUpDate}
              onChange={(e) => setNextFollowUpDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              className="input"
            />
            <p className="text-xs text-gray-500 mt-1">
              Optional - Set a reminder for your next visit
            </p>
          </div>

          {/* Photo Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Evidence Photos
            </label>
            <p className="text-xs text-gray-500 mb-2">
              Take photos of gift cards, food, baskets, or other items you brought
            </p>

            {/* Photo previews */}
            {photoPreviewUrls.length > 0 && (
              <div className="flex gap-2 flex-wrap mb-3">
                {photoPreviewUrls.map((url, index) => (
                  <div key={index} className="relative">
                    <img
                      src={url}
                      alt={`Photo ${index + 1}`}
                      className="w-20 h-20 object-cover rounded-lg border border-gray-200"
                    />
                    <button
                      type="button"
                      onClick={() => removePhoto(index)}
                      className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full text-xs flex items-center justify-center"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}

            {photos.length < 5 && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  multiple
                  onChange={handlePhotoSelect}
                  className="hidden"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="btn btn-secondary w-full"
                >
                  <Camera size={18} />
                  {photos.length === 0 ? 'Add Photos' : 'Add More Photos'}
                </button>
              </>
            )}
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary flex-1"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving || !visitDate}
              className="btn btn-primary flex-1"
            >
              {isSaving ? (
                <>
                  <RefreshCw className="animate-spin" size={18} />
                  Saving...
                </>
              ) : (
                <>
                  <CheckCircle size={18} />
                  Log Visit
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function SendReferralModal({ company, onClose, onSuccess }: SendReferralModalProps) {
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [description, setDescription] = useState('');
  const [business, setBusiness] = useState<Business>('azdisaster');
  const [isSending, setIsSending] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!clientName || !description) return;

    setIsSending(true);
    try {
      const result = await createOutboundReferral({
        companyId: company.id,
        business,
        clientName,
        clientPhone: clientPhone || undefined,
        clientEmail: clientEmail || undefined,
        description,
      });

      if (result.success && result.referralId) {
        toast.success('Referral sent successfully!');
        // Create a mock referral object for optimistic UI update
        const newReferral: OutboundReferral = {
          id: result.referralId,
          companyId: company.id,
          companyName: company.name,
          business,
          clientName,
          clientPhone: clientPhone || undefined,
          clientEmail: clientEmail || undefined,
          description,
          status: 'sent',
          sentBy: '',
          sentByName: 'You',
          sentAt: new Date(),
          createdAt: new Date(),
          updatedAt: new Date(),
        };
        onSuccess(newReferral);
      } else {
        toast.error(result.error || 'Failed to send referral');
      }
    } catch (error) {
      console.error('Failed to send referral:', error);
      toast.error('Failed to send referral');
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
        <h3 className="text-lg font-semibold text-gray-900">Send Referral to {company.name}</h3>
        <p className="mt-1 text-sm text-gray-600">
          Send a job lead to this partner. They'll receive an email notification.
        </p>

        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Business *
            </label>
            <select
              value={business}
              onChange={(e) => setBusiness(e.target.value as Business)}
              className="input"
              required
            >
              <option value="azdisaster">ACR</option>
              <option value="blacksmith">Blacksmith</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Client Name *
            </label>
            <input
              type="text"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              placeholder="Enter the client's name"
              className="input"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Client Phone
              </label>
              <input
                type="tel"
                value={clientPhone}
                onChange={(e) => setClientPhone(e.target.value)}
                placeholder="(555) 123-4567"
                className="input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Client Email
              </label>
              <input
                type="email"
                value={clientEmail}
                onChange={(e) => setClientEmail(e.target.value)}
                placeholder="client@email.com"
                className="input"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description *
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the job or situation..."
              rows={3}
              className="input"
              required
            />
          </div>

          <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
            <p className="text-sm text-purple-800">
              <strong>Note:</strong> An email will be sent to {company.name} with this client information.
              They can update the status through their partner portal.
            </p>
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary flex-1"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSending || !clientName || !description}
              className="btn btn-primary flex-1"
            >
              {isSending ? (
                <>
                  <RefreshCw className="animate-spin" size={18} />
                  Sending...
                </>
              ) : (
                <>
                  <Send size={18} />
                  Send Referral
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ===========================================
// Company Notes Section (Phase 5)
// ===========================================

function CompanyNotesSection({
  companyId,
  initialNotes,
  canEdit,
}: {
  companyId: string;
  initialNotes: string;
  canEdit: boolean;
}) {
  const [notes, setNotes] = React.useState(initialNotes);
  const [isSaving, setIsSaving] = React.useState(false);
  const [isDirty, setIsDirty] = React.useState(false);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateCompanyService(companyId, { notes } as any);
      setIsDirty(false);
      toast.success('Notes saved');
    } catch (err) {
      toast.error('Failed to save notes');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold text-gray-900">Company Notes</h2>
        {canEdit && isDirty && (
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="btn btn-primary text-sm py-1.5 px-3"
          >
            {isSaving ? <RefreshCw className="animate-spin" size={14} /> : 'Save Notes'}
          </button>
        )}
      </div>
      {canEdit ? (
        <textarea
          value={notes}
          onChange={(e) => {
            setNotes(e.target.value);
            setIsDirty(true);
          }}
          onBlur={() => {
            if (isDirty) handleSave();
          }}
          rows={4}
          className="input w-full"
          placeholder="Add notes about this company..."
        />
      ) : (
        <p className="text-sm text-gray-700 whitespace-pre-wrap">
          {notes || 'No notes yet.'}
        </p>
      )}
    </div>
  );
}
